import { useState, useEffect } from "react";
import { X, BookOpen, Edit2, Trash2, Plus, ArrowLeft } from "lucide-react";
import SporterSchemaEditModal from "./SporterSchemaEditModal";

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = "#C9A84C";

export default function CursistSchemaPanel({ cursist, token, onClose }) {
  const [schemas, setSchemas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editSchemaId, setEditSchemaId] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const laadSchemas = () => {
    setLoading(true);
    fetch(`${API}/api/admin/sporter/${cursist.id}/custom-schemas`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => { setSchemas(d.schemas || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { laadSchemas(); }, [cursist.id]);

  const verwijderSchema = async (schemaId) => {
    if (!window.confirm("Schema verwijderen van deze cursist?")) return;
    setDeleting(schemaId);
    await fetch(`${API}/api/admin/sporter/${cursist.id}/custom-schemas/${schemaId}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    setDeleting(null);
    laadSchemas();
  };

  const niveauKleur = (n) => {
    if (n === "Beginner") return "text-green-400 bg-green-500/10";
    if (n === "Advanced") return "text-red-400 bg-red-500/10";
    return "text-blue-400 bg-blue-500/10";
  };

  return (
    <>
      <div
        className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center"
        style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)" }}
        onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div
          data-testid="cursist-schema-panel"
          className="w-full sm:w-[580px] max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl border border-white/10 flex flex-col"
          style={{ background: "#0d0d0d" }}
        >
          {/* Header */}
          <div className="sticky top-0 z-10 flex items-center justify-between px-6 pt-5 pb-4 border-b border-white/8"
            style={{ background: "#0d0d0d" }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center text-base font-black text-black shrink-0"
                style={{ background: GOLD }}>
                {(cursist.name || cursist.naam || "?")[0].toUpperCase()}
              </div>
              <div>
                <h2 className="text-white font-black text-base">{cursist.name || cursist.naam}</h2>
                <p className="text-zinc-600 text-xs">{cursist.email} · Schema's beheren</p>
              </div>
            </div>
            <button
              data-testid="cursist-schema-panel-sluiten"
              onClick={onClose}
              className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors"
            >
              <X size={15} className="text-zinc-400" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-5 flex-1 space-y-3">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-7 h-7 rounded-full border-2 border-t-transparent animate-spin"
                  style={{ borderColor: GOLD, borderTopColor: "transparent" }} />
              </div>
            ) : schemas.length === 0 ? (
              <div className="text-center py-12 rounded-2xl border border-white/5">
                <BookOpen size={32} className="text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-500 text-sm font-semibold">Geen schema's toegewezen</p>
                <p className="text-zinc-700 text-xs mt-1">Wijs een template toe via de "Schema Templates" tab.</p>
              </div>
            ) : (
              schemas.map(s => (
                <div
                  key={s.id}
                  data-testid={`cursist-schema-${s.id}`}
                  className="flex items-start justify-between p-4 rounded-2xl border border-white/8 bg-white/2 hover:bg-white/4 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-bold text-sm truncate">{s.naam}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {s.niveau && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-md font-bold ${niveauKleur(s.niveau)}`}>
                          {s.niveau}
                        </span>
                      )}
                      {s.doel && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-md font-bold" style={{ background: `${GOLD}15`, color: GOLD }}>
                          {s.doel}
                        </span>
                      )}
                      <span className="text-zinc-600 text-xs">{s.trainingsdagen} dagen · {s.weken} weken</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0 ml-3">
                    <button
                      data-testid={`bewerk-schema-${s.id}`}
                      onClick={() => setEditSchemaId(s.id)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all hover:opacity-90"
                      style={{ background: `${GOLD}15`, color: GOLD }}
                    >
                      <Edit2 size={11} /> Bewerk
                    </button>
                    <button
                      onClick={() => verwijderSchema(s.id)}
                      disabled={deleting === s.id}
                      className="w-7 h-7 rounded-xl flex items-center justify-center text-zinc-600 hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-40"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-white/8 shrink-0">
            <p className="text-zinc-700 text-xs text-center">
              Meer schema's toevoegen? Gebruik de <span className="text-zinc-500">"Schema Templates"</span> tab → "Aan sporter toevoegen".
            </p>
          </div>
        </div>
      </div>

      {/* Schema Edit Modal */}
      {editSchemaId && (
        <SporterSchemaEditModal
          schemaId={editSchemaId}
          sporter={cursist}
          token={token}
          onClose={() => setEditSchemaId(null)}
          onSaved={() => { setEditSchemaId(null); laadSchemas(); }}
        />
      )}
    </>
  );
}
